import { AVATAR_BASE_URL } from "@Pages/PropertyPage/components/ReviewsSectionV2/avatars";

/**
 * The signed-in account's phone as people write it: "+44 7700900123". The account API
 * gives `phone_details` ({ country_code, national }) and a raw `phone` without the "+".
 * The national number's leading 0 is a trunk prefix and is dropped, except where the
 * country keeps it in international form (Italy, San Marino, Vatican).
 */
export const phoneOf = (d: any, raw?: string): string => {
  if (d?.country_code && d?.national) {
    const keepZero = ["39", "378", "379"].includes(String(d.country_code).replace("+", ""));
    const national = String(d.national).replace(/\s/g, "");
    return `+${String(d.country_code).replace("+", "")} ${
      keepZero ? national : national.replace(/^0+/, "")
    }`;
  }
  return raw ? `+${raw}` : "";
};

/** The account's display name: its full name, else first and last name together. */
export const nameOf = (user: any): string =>
  String(user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ")).trim();

/** Up to two initials for an avatar without a photo ("Alex Carter" → "AC"). */
export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

/**
 * The illustrated avatars the reviews use (ReviewsSectionV2/avatars), split by who they
 * show. #1 reads as either, so it is in neither set.
 */
const FEMALE_AVATARS = [2, 5, 9, 11, 13, 15, 17];
const MALE_AVATARS = [3, 4, 6, 7, 8, 10, 12, 14, 16, 18];

/**
 * The account's picture: its own photo if it has one, else one of the illustrated
 * avatars for the gender the account states, picked from a hash of the account's id
 * (else email, else name) so the same account always gets the same face — it never
 * changes between visits. Without a stated gender there is no avatar (the caller shows
 * initials): a face is never guessed from a name.
 */
export const avatarOf = (user: any): string => {
  if (user?.profile_image_url) return user.profile_image_url;
  const gender = String(user?.gender || "")
    .trim()
    .toLowerCase();
  let set: number[] | null = null;
  if (/^(f|female|woman)$/.test(gender)) set = FEMALE_AVATARS;
  else if (/^(m|male|man)$/.test(gender)) set = MALE_AVATARS;
  if (!set) return "";
  const seed = String(user?.id ?? user?.email ?? nameOf(user));
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 2147483647;
  return `${AVATAR_BASE_URL}/avatar-${set[Math.abs(hash) % set.length]}.png`;
};
